import type { RootState } from '@generalTypes/rootStateTypes';
import { getAngularService } from '@kathondvla/react-shared-components/src/helpers/angularReactHelper';
import { selectIsRoot } from '@newStore/documentUI/documentUISelectors';
import { loadReferenceFrame } from '@newStore/referenceFrames/referenceFramesState';
import type { EditAsideConcordanties } from '@nodeTypeConfig/configTypes';
import {
  addRelationAction,
  editZillCurriculumAsFrame,
  patchRelationAction,
  removeRelationAction,
  setSelectedZillCurriculumAsFrame,
} from '@store/actions/documentActions';
import { selectCurrentEditingNode } from '@store/selectors/asideSelectors';
import { any } from 'prop-types';
import type React from 'react';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from '@kathondvla/react-shared-components/src/components';
import ModalErrorBoundary from '@UI/modals/ModalErrorBoundary';
import ConcordantieModal from '@UI/modals/ConcordantieModal';
import OdetModal from '@UI/modals/OdetModal';
import { useEditHref } from '../../../hooks/UseEditHref';
import type { Concordantie } from './asideConcordantiesSelectors';
import {
  selectAsideConcordanties,
  selectRootOdet,
  selectRootOdetHref,
} from './asideConcordantiesSelectors';

const AsideConcordanties: React.FC<{
  config: EditAsideConcordanties;
  readonly: boolean;
}> = ({ config, readonly }) => {
  const editHref = useEditHref();
  const dispatch = useDispatch();
  const confirmationService = getAngularService('ConfirmationService');
  const odetModalRef = useRef<Modal>();
  const concordantieModalRef = useRef<Modal>();

  const isRoot = useSelector((state: RootState) => selectIsRoot(state, editHref));
  const rootOdetHref = useSelector((state: RootState) => selectRootOdetHref(state));
  const rootOdet = useSelector((state: RootState) => selectRootOdet(state));

  // the odet is not loaded if user changes it in root element
  useEffect(() => {
    if (rootOdetHref) {
      dispatch(loadReferenceFrame({ referenceFrame: rootOdetHref }));
    }
  }, [dispatch, rootOdetHref]);

  const concordanties = useSelector((state: RootState) =>
    selectAsideConcordanties(state, editHref),
  );

  const concordantieNotAlreadyAdded = (concordantieToAdd) => {
    return !concordanties.some((concordantie) => {
      return concordantieToAdd.key === concordantie.contentKey;
    });
  };

  const isLoading = isRoot
    ? !rootOdet?.isLoaded && concordanties.length > 0
    : rootOdet && !rootOdet.isLoaded;

  const currentNode = useSelector((state: RootState) => selectCurrentEditingNode(state));

  const concordantiesEditionDisabled =
    currentNode.type === 'CURRICULUM_ZILL' && concordanties.length > 0;

  const deleteConcordantie = async (concordantie: Concordantie) => {
    const confirmed = await confirmationService.confirmDeletion();
    if (confirmed) {
      dispatch(removeRelationAction(concordantie.key));
    }
  };

  const openConcordantieModal = (onSubmit, concordantie: Concordantie | undefined = undefined) => {
    const modalRef = isRoot ? odetModalRef : concordantieModalRef;
    modalRef.current?.open({
      concordantie,
      onSubmit: (response) => {
        if (response) {
          onSubmit(response, concordantie);
        }
      },
    });
  };

  const editConcordantie = (response, concordantie: Concordantie) => {
    const action = isRoot ? editZillCurriculumAsFrame : patchRelationAction;
    const selectedRelation = isRoot ? response.odet : response.endterms;

    if (concordantieNotAlreadyAdded(selectedRelation)) {
      const patchToApply = {
        to: {
          href: selectedRelation.$$meta.permalink,
          $$expanded: selectedRelation,
        },
      };

      dispatch(action(concordantie.key, patchToApply));
    }
  };

  const addConcordanties = async (response) => {
    const concordantiesToAdd = isRoot ? [response.odet] : response.endterms;
    const action = isRoot ? setSelectedZillCurriculumAsFrame : addRelationAction;

    concordantiesToAdd.filter(concordantieNotAlreadyAdded).forEach((target) => {
      const newRelation = {
        relationtype: 'IS_VERSION_OF',
        strength: 'MEDIUM',
        from: {
          href: editHref,
        },
        to: {
          href: target.$$meta.permalink,
          $$expanded: target,
        },
      };

      dispatch(action(newRelation));
    });
  };

  return (
    <div className="panel panel-default">
      <div className="panel-heading">
        <div className="panel-controls panel-controls-left">
          <h3 className="panel-title">Concordanties</h3>
        </div>
        <div className="panel-controls panel-controls-right">
          <div className="btn-toolbar">
            {!concordantiesEditionDisabled && (
              <a
                className="btn btn-default"
                onClick={() => {
                  openConcordantieModal(addConcordanties);
                }}
              >
                <span className="glyphicon glyphicon-add" />
                <span>Voeg toe</span>
              </a>
            )}
          </div>
        </div>
      </div>
      {isLoading ? (
        <div className="shimmer narrowLine" />
      ) : (
        <div className="structure">
          {concordanties.map((concordantie) => (
            <div className="row table-row" key={concordantie.key}>
              <div className="col-md-2" style={{ fontWeight: 'bold' }}>
                {concordantie.goalIdentifier}
              </div>
              <div className="col-md-10">
                {concordantie.title || 'Relatie niet gevonden in ODET document'}
              </div>
              <div className="btn-group btn-group-xs row-actions" role="group" aria-label="...">
                {concordantie.title && (
                  <button
                    type="button"
                    className="btn btn-default"
                    onClick={() => {
                      openConcordantieModal(editConcordantie, concordantie);
                    }}
                  >
                    <span className="glyphicon glyphicon-edit" />
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-default"
                  onClick={() => {
                    deleteConcordantie(concordantie);
                  }}
                >
                  <span className="glyphicon glyphicon-delete" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      <Modal
        ref={odetModalRef}
        size="sm"
        component={<OdetModal />}
        errorBoundaryComponent={ModalErrorBoundary}
      />
      <Modal
        ref={concordantieModalRef}
        size="sm"
        component={<ConcordantieModal />}
        errorBoundaryComponent={ModalErrorBoundary}
      />
    </div>
  );
};

AsideConcordanties.displayName = 'AsideConcordanties';

export default AsideConcordanties;

AsideConcordanties.propTypes = {
  config: any,
  readonly: any,
};
